import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';
import EntityCompliancePanel from '../components/EntityCompliancePanel';

const FACILITY_TYPES = [
  { value: 'office', label: 'Office', color: '#3b82f6' },
  { value: 'lot', label: 'Lot', color: '#10b981' },
  { value: 'yard', label: 'Yard', color: '#d97706' },
  { value: 'storage', label: 'Storage', color: '#8b5cf6' },
  { value: 'other', label: 'Other', color: '#6b7280' },
];

const typeColorMap = Object.fromEntries(FACILITY_TYPES.map(t => [t.value, t.color]));

const EMPTY_FORM = {
  name: '', facility_type: 'office', address: '', city: '', state: 'TX', zip: '',
  phone: '', manager_employee_id: '', notes: '',
};

export default function FacilityListPage() {
  const { can } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getFacilities();
      setFacilities(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load facilities');
    }
    setLoading(false);
  }, []);

  const loadEmployees = useCallback(async () => {
    try {
      const data = await api.getEmployees();
      setEmployees(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  }

  function openEdit(fac) {
    setEditing(fac);
    setForm({
      name: fac.name || '',
      facility_type: fac.facility_type || 'office',
      address: fac.address || '',
      city: fac.city || '',
      state: fac.state || 'TX',
      zip: fac.zip || '',
      phone: fac.phone || '',
      manager_employee_id: fac.manager_employee_id || '',
      notes: fac.notes || '',
    });
    setError('');
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, manager_employee_id: form.manager_employee_id || null };
      if (editing) {
        await api.updateFacility(editing.id, payload);
        setSuccess('Facility updated');
      } else {
        await api.createFacility(payload);
        setSuccess('Facility added');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save facility');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteFacility(id);
      setDeleteConfirm(null);
      setSuccess('Facility deactivated');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to deactivate facility');
      setDeleteConfirm(null);
    }
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  if (!can('manage_admin')) {
    return <div className="page-content"><p>You do not have permission to access this page.</p></div>;
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Facilities</h1>
        <button className="btn btn-gold" onClick={openAdd}>+ Add Facility</button>
      </div>

      {error && !showModal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading ? (
        <div className="loading-spinner">Loading facilities...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Address</th>
                <th>City</th>
                <th>Phone</th>
                <th>Manager</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {facilities.length === 0 ? (
                <tr><td colSpan="7" className="text-center">No facilities found. Add one to get started.</td></tr>
              ) : facilities.map(fac => (
                <tr key={fac.id}>
                  <td><strong>{fac.name}</strong></td>
                  <td>
                    <span className="status-pill" style={{
                      background: `${typeColorMap[fac.facility_type] || '#6b7280'}18`,
                      color: typeColorMap[fac.facility_type] || '#6b7280',
                    }}>
                      {fac.facility_type}
                    </span>
                  </td>
                  <td>{fac.address || '\u2014'}</td>
                  <td>{fac.city || '\u2014'}</td>
                  <td>{fac.phone || '\u2014'}</td>
                  <td>{fac.manager_name || '\u2014'}</td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(fac)}>Edit</button>
                    {' '}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(fac)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editing ? 'Edit Facility' : 'Add Facility'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

            <div className="form-grid">
              <div className="form-group">
                <label>Facility Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <label>Facility Type</label>
                <select name="facility_type" value={form.facility_type} onChange={handleChange} className="form-input">
                  {FACILITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input name="address" value={form.address} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>City</label>
                <input name="city" value={form.city} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>State</label>
                <input name="state" value={form.state} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Zip</label>
                <input name="zip" value={form.zip} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Manager</label>
                <select name="manager_employee_id" value={form.manager_employee_id} onChange={handleChange} className="form-input">
                  <option value="">Select manager...</option>
                  {employees.filter(e => e.employment_status === 'active').map(e => (
                    <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} className="form-input" rows={3} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Facility' : 'Add Facility')}
              </button>
            </div>
          </form>

          {editing && (
            <EntityCompliancePanel
              entityType="facility"
              entityId={editing.id}
              entityName={editing.name}
            />
          )}
        </Modal>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <Modal title="Delete Facility" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to deactivate <strong>{deleteConfirm.name}</strong>?</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
